<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">Organization</div>
        <h3 class="duallo-table-title">Users</h3>
      </div>
      <DisplayOverlayWindowButton v-if="can(Permissions.UserCreate)" ref="newUserWindow" buttonText="New User"
        iconName="mdi-plus" windowTitle="New User">
        <CreateNewUserComponent @onUserCreated="createdUserAsync()" @cancel="closeWindow()" />
      </DisplayOverlayWindowButton>
    </header>

    <div class="duallo-table-card">
      <div v-if="isLoadingMembers" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
      </div>

      <div v-else-if="loadError" class="duallo-state-block empty-state">
        <v-icon size="40" color="#ff8a80">mdi-alert-circle-outline</v-icon>
        <p class="empty-title">Couldn't load users</p>
        <p class="empty-subtitle">Something went wrong fetching users for this organization.</p>
        <button type="button" class="duallo-btn duallo-btn--ghost retry-btn" @click="fetchMembersAsync">
          <v-icon size="16">mdi-refresh</v-icon>
          <span>Try again</span>
        </button>
      </div>

      <div v-else-if="!members || members.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-account-group-outline</v-icon>
        <p class="empty-title">No users found</p>
        <p class="empty-subtitle">Add a user to give them access to this organization.</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-avatar"></th>
                <th class="col-name">Name</th>
                <th class="col-email">Email</th>
                <th class="col-role">Role</th>
                <th class="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="member in members" :key="member.userId">
                <td class="cell-avatar">
                  <div class="member-avatar">
                    <img v-if="member.picture" :src="member.picture" alt="User" />
                    <v-icon v-else size="18" color="#4a5a60">mdi-account</v-icon>
                  </div>
                </td>
                <td class="cell-name">{{ member.name }}</td>
                <td class="cell-email">{{ member.email }}</td>
                <td class="cell-role">
                  <span class="role-pill">{{ getUserTitleFromRoleName(member.role.name) }}</span>
                </td>
                <td class="cell-actions">
                  <DisplayOverlayWindowButton buttonText="Details" windowTitle="Member Details">
                    <AuthMemberListItem :member="member" @onMemberDeleted="fetchMembersAsync"
                      @onMemberModified="fetchMembersAsync" />
                  </DisplayOverlayWindowButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <article v-for="member in members" :key="member.userId" class="duallo-list-card">
            <div class="card-head">
              <div class="member-avatar lg">
                <img v-if="member.picture" :src="member.picture" alt="User" />
                <v-icon v-else size="22" color="#4a5a60">mdi-account</v-icon>
              </div>
              <div class="card-meta">
                <div class="card-name">{{ member.name }}</div>
                <div class="card-email">{{ member.email }}</div>
                <span class="role-pill">{{ getUserTitleFromRoleName(member.role.name) }}</span>
              </div>
            </div>
            <div class="card-actions">
              <DisplayOverlayWindowButton buttonText="Details" windowTitle="Member Details">
                <AuthMemberListItem :member="member" @onMemberDeleted="fetchMembersAsync"
                  @onMemberModified="fetchMembersAsync" />
              </DisplayOverlayWindowButton>
            </div>
          </article>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { defineOptions, onMounted, ref, computed } from 'vue';
import { useDisplay } from 'vuetify';
import DisplayOverlayWindowButton from '../buttons/DisplayOverlayWindowButton.vue';
import AuthZeroService from '@/services/AuthZeroService';
import CreateNewUserComponent from '../CreateNewUserComponent.vue';
import { getUserTitleFromRoleName } from '@/utils/AuthRolesMap';
import { useOrganizationStore } from '@/store/organizationStore';
import AuthMemberListItem from '../list-items/AuthMemberListItem.vue';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';

defineOptions({ name: "ManageAuthMemberList" })

const { can } = usePermissions();
const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const newUserWindow = ref(null)
const isLoadingMembers = ref(true)
const loadError = ref(false)
const members = ref(null)
const organizationStore = useOrganizationStore();

onMounted(async () => { await fetchMembersAsync(); })

const closeWindow = () => { newUserWindow.value?.closeWindow(); }

const fetchMembersAsync = async () => {
  isLoadingMembers.value = true
  loadError.value = false
  try {
    const orgId = organizationStore.authZeroOrgId;
    members.value = await AuthZeroService.getMembersByOrg(orgId);
  } catch (err) {
    console.error('Error fetching members', err);
    loadError.value = true
  } finally {
    isLoadingMembers.value = false
  }
}

const createdUserAsync = async () => {
  closeWindow();
  await fetchMembersAsync();
}
</script>

<style scoped>
/* Shell (.duallo-admin-table / -table-header / -eyebrow / -table-title), loading/empty
   container (.duallo-state-block), skeleton, table wrap + base, and card-list now come
   from the shared duallo-components.css. Kept below: this panel's own card surface,
   empty-state text, avatars, role pill, and the per-file overrides. */


/* Shared skeleton rows are 48px; this user list uses taller rows. */
.duallo-row-skeleton { height: 56px; }

.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle { font-size: 0.88rem; margin: 0; }

.retry-btn { margin-top: 16px; }

.col-avatar { width: 60px; }
.col-name { min-width: 120px; }
.col-email { min-width: 180px; }
.col-role { width: 140px; }
.col-actions { width: 120px; text-align: right; }

/* Shared table cells are 12px 16px; this list uses tighter vertical padding. */
.duallo-data-table td { padding: 10px 16px; }

.cell-name { color: #fff; font-weight: 600; }
.cell-email { color: #b0bec5; }
.cell-actions { text-align: right; }

.member-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: #1c272c;
  border: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}

.member-avatar.lg {
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
}

.member-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.role-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(102, 199, 180, 0.12);
  border: 1px solid rgba(102, 199, 180, 0.25);
  color: var(--duallo-accent-muted);
  font-size: 0.72rem;
  font-weight: 700;
}

/* Shared list card uses gap:8 and no flex-basis; preserve this list's spacing. */
.duallo-list-card { gap: 10px; flex: 0 0 auto; }

.card-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.card-name {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-email {
  font-size: 0.85rem;
  color: #b0bec5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta .role-pill { align-self: flex-start; margin-top: 2px; }

.card-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
